import {
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
} from '@mui/x-data-grid-pro'
import type { InventoryV3Row } from './buildInventoryV3Rows'
import { inventoryV3DateOperatorMatches, isInventoryV3DateFilterItem, isInventoryV3DateFilterReady } from './inventoryV3DateFilter'

const OPERATORS_WITHOUT_VALUE = new Set(['isEmpty', 'isNotEmpty'])

export const isInventoryV3FilterItemActive = (item: GridFilterItem | undefined): boolean => {
  if (!item) return false
  if (isInventoryV3DateFilterItem(item)) return isInventoryV3DateFilterReady(item)
  if (OPERATORS_WITHOUT_VALUE.has(item.operator)) return true
  if (item.value === null || item.value === undefined) return false
  if (item.operator === 'isAnyOf') {
    return Array.isArray(item.value) ? item.value.length > 0 : String(item.value).trim() !== ''
  }
  return String(item.value).trim() !== ''
}

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

const asString = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) return value.toISOString()
  return String(value)
}

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.replace(/,/g, ''))
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const asTime = (value: unknown): number | null => {
  if (value instanceof Date) {
    const time = value.getTime()
    return Number.isNaN(time) ? null : time
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const time = Date.parse(value)
    return Number.isNaN(time) ? null : time
  }
  return null
}

const startOfDay = (time: number): number => {
  const date = new Date(time)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

const getCellValue = (
  row: InventoryV3Row,
  field: string,
  column: GridColDef<InventoryV3Row> | undefined,
): unknown => {
  const raw = (row as unknown as Record<string, unknown>)[field]
  const valueGetter = column?.valueGetter
  if (!valueGetter) return raw
  try {
    return valueGetter(raw as never, row, column, undefined as never)
  } catch {
    return raw
  }
}

const matchesOperator = (cellValue: unknown, item: GridFilterItem): boolean => {
  const operator = item.operator
  const filterValue = item.value

  const dateMatch = inventoryV3DateOperatorMatches(cellValue, operator, filterValue)
  if (dateMatch !== null) return dateMatch

  if (operator === 'isEmpty') return isEmptyValue(cellValue)
  if (operator === 'isNotEmpty') return !isEmptyValue(cellValue)

  if (operator === 'isAnyOf') {
    const options = Array.isArray(filterValue)
      ? filterValue.map(option => asString(option).toLowerCase())
      : [asString(filterValue).toLowerCase()]
    return options.includes(asString(cellValue).toLowerCase())
  }

  if (
    operator === '=' ||
    operator === '!=' ||
    operator === '>' ||
    operator === '>=' ||
    operator === '<' ||
    operator === '<='
  ) {
    const cellNumber = asNumber(cellValue)
    const filterNumber = asNumber(filterValue)
    if (cellNumber === null || filterNumber === null) return false
    if (operator === '=') return cellNumber === filterNumber
    if (operator === '!=') return cellNumber !== filterNumber
    if (operator === '>') return cellNumber > filterNumber
    if (operator === '>=') return cellNumber >= filterNumber
    if (operator === '<') return cellNumber < filterNumber
    return cellNumber <= filterNumber
  }

  if (operator === 'is' || operator === 'not') {
    const cellTime = asTime(cellValue)
    const filterTime = asTime(filterValue)
    if (cellTime !== null && filterTime !== null) {
      const equal = startOfDay(cellTime) === startOfDay(filterTime)
      return operator === 'is' ? equal : !equal
    }
    const equal = asString(cellValue).toLowerCase() === asString(filterValue).toLowerCase()
    return operator === 'is' ? equal : !equal
  }

  const cell = asString(cellValue).toLowerCase()
  const filter = asString(filterValue).toLowerCase()
  if (operator === 'contains') return cell.includes(filter)
  if (operator === 'equals') return cell === filter
  if (operator === 'startsWith') return cell.startsWith(filter)
  if (operator === 'endsWith') return cell.endsWith(filter)

  return true
}

export const applyInventoryV3FilterModel = (
  rows: InventoryV3Row[],
  filterModel: GridFilterModel,
  columns: GridColDef<InventoryV3Row>[],
): InventoryV3Row[] => {
  const activeItems = (filterModel.items ?? []).filter(isInventoryV3FilterItemActive)
  if (activeItems.length === 0) return rows

  const columnByField = new Map(columns.map(column => [column.field, column]))
  const useOr = filterModel.logicOperator === GridLogicOperator.Or

  return rows.filter(row => {
    const results = activeItems.map(item => {
      const column = columnByField.get(item.field)
      return matchesOperator(getCellValue(row, item.field, column), item)
    })
    return useOr ? results.some(Boolean) : results.every(Boolean)
  })
}
